import React, { useState } from "react";
import { blogPosts, blogCategories } from "../data/mock";
import { Calendar, Clock, Tag, ArrowRight, Sparkles, Mail } from "lucide-react";
import { FEATURES } from "../config/features";
import { API_BASE_URL } from "../config/api";

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState("All Posts");
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [email, setEmail] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState({ message: "", isError: false });
  const [selectedPost, setSelectedPost] = useState(null);

  const filteredPosts = selectedCategory === "All Posts" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPosts = blogPosts.filter(post => post.featured);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setSubscribeStatus({ message: "Please enter a valid email address", isError: true });
      return;
    }

    if (!consentChecked) {
      setSubscribeStatus({ message: "Please acknowledge our data policies to continue", isError: true });
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/subscribers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setSubscribeStatus({ message: data.message, isError: false });
        setEmail("");
        setTimeout(() => {
          setShowSubscribeModal(false);
          setSubscribeStatus({ message: "", isError: false });
        }, 3000);
      } else {
        setSubscribeStatus({ message: data.message || "Subscription failed. Please try again.", isError: !data.success });
      }
    } catch (error) {
      setSubscribeStatus({ message: "Connection error. Please try again later.", isError: true });
    }
  };

  if (selectedPost) {
    return (
      <section className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50 py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          {/* Back Button */}
          <button
            onClick={() => setSelectedPost(null)}
            className="flex items-center space-x-2 text-brand-main hover:text-fire-main transition-colors mb-8 group"
          >
            <ArrowRight size={20} className="rotate-180 group-hover:-translate-x-1 transition-transform" />
            <span className="font-light">Back to INSPIYAH MAIL</span>
          </button>

          {/* Article Header */}
          <article className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-brand-deep via-fire-main to-spirit-teal p-12 text-white text-center">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Tag size={16} />
                <span className="text-sm uppercase tracking-widest">{selectedPost.category}</span>
              </div>
              <h1 className="text-3xl lg:text-4xl font-light leading-relaxed">
                {selectedPost.title}
              </h1>
              <div className="flex items-center justify-center space-x-6 mt-6 text-sm">
                <div className="flex items-center space-x-2">
                  <Calendar size={16} />
                  <span>{new Date(selectedPost.publishDate).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock size={16} />
                  <span>{selectedPost.readTime}</span>
                </div>
              </div>
            </div>

            <div className="p-12">
              <div className="prose prose-lg max-w-none">
                <p className="text-xl text-gray-600 font-light leading-relaxed italic mb-8">
                  {selectedPost.excerpt}
                </p>
                <div className="text-gray-700 font-light leading-relaxed space-y-6">
                  {selectedPost.content.split('\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                  <Tag size={16} className="text-gray-400" />
                  <span className="text-sm font-light text-gray-600">Tags</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedPost.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-gradient-to-r from-brand-lighter to-fire-lighter text-brand-deep text-sm font-light rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Mystical INSPIYAH Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-900 via-purple-900 to-black">
        <div className="absolute inset-0 bg-gradient-to-tr from-yellow-500/15 via-orange-500/20 to-red-500/10 animate-pulse" style={{animationDuration: '6s'}} />
        
        {/* Sacred fire elements */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-orange-600/30 via-red-500/15 to-transparent">
          <div className="absolute bottom-0 left-1/6 w-3 h-16 bg-orange-400 rounded-full animate-bounce opacity-60" style={{animationDelay: '0s'}} />
          <div className="absolute bottom-0 left-1/3 w-4 h-20 bg-yellow-400 rounded-full animate-bounce opacity-70" style={{animationDelay: '0.5s'}} />
          <div className="absolute bottom-0 left-1/2 w-5 h-24 bg-red-400 rounded-full animate-bounce opacity-80" style={{animationDelay: '1s'}} />
          <div className="absolute bottom-0 right-1/3 w-3 h-18 bg-orange-400 rounded-full animate-bounce opacity-60" style={{animationDelay: '1.5s'}} />
          <div className="absolute bottom-0 right-1/6 w-4 h-16 bg-yellow-400 rounded-full animate-bounce opacity-50" style={{animationDelay: '2s'}} />
        </div>
      </div>
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 border border-brand-light rounded-full opacity-20 animate-pulse" style={{animationDuration: '4s'}} />
        <div className="absolute bottom-20 right-10 w-24 h-24 border border-fire-light rounded-full opacity-20 animate-pulse" style={{animationDuration: '6s'}} />
        <div className="absolute top-1/2 left-1/4 w-3 h-3 bg-spirit-gold rounded-full animate-bounce" style={{animationDelay: '1s'}} />
        <div className="absolute top-1/3 right-1/3 w-2 h-2 bg-brand-main rounded-full animate-bounce" style={{animationDelay: '2s'}} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <Mail size={24} className="text-fire-main" />
            <Sparkles size={24} className="text-brand-main" />
            <Mail size={24} className="text-spirit-teal" />
          </div>
          
          <h2 className="text-5xl lg:text-6xl font-light tracking-tight mb-6 relative">
            <span className="bg-gradient-to-r from-yellow-300 via-orange-400 to-red-500 bg-clip-text text-transparent animate-pulse drop-shadow-2xl">
              INSPIYAH MAIL
            </span>
            <div className="absolute -inset-2 bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-red-500/20 blur-xl -z-10 animate-pulse" />
          </h2>
          
          <p className="text-2xl lg:text-3xl font-light mb-4 tracking-wide">
            <span className="bg-gradient-to-r from-orange-200 via-yellow-300 to-orange-300 bg-clip-text text-transparent">
              MIND UNPLUGGED FROM INSPIYAH TO BLOG
            </span>
          </p>
          
          <p className="text-lg max-w-3xl mx-auto font-light leading-relaxed">
            <span className="bg-gradient-to-r from-orange-200 to-yellow-200 bg-clip-text text-transparent">
              Spiritual insights, transformation stories, and ancient wisdom for modern living. 
              From the mind of <span className="italic bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent font-medium">Keeper Of The Fiyah</span> comes 
              raw truth about faith, growth, and authentic living through <span className="font-medium">FLY FREQUENCY</span>.
            </span>
          </p>
        </div>

        {/* Featured Posts - Hidden in Production */}
        {FEATURES.showAllFeatures && featuredPosts.length > 0 && (
          <div className="mb-16">
            <h3 className="text-2xl font-light text-brand-deep mb-8 text-center tracking-wide">
              Featured Inspiration
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              {featuredPosts.map((post) => (
                <article 
                  key={post.id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group cursor-pointer"
                  onClick={() => setSelectedPost(post)}
                >
                  <div className="bg-gradient-to-r from-brand-main to-fire-main p-6 text-white">
                    <div className="flex items-center space-x-2 mb-3">
                      <Sparkles size={16} />
                      <span className="text-sm uppercase tracking-widest">Featured</span>
                    </div>
                    <h4 className="text-xl font-light leading-relaxed group-hover:scale-105 transition-transform duration-300">
                      {post.title}
                    </h4>
                  </div>
                  
                  <div className="p-6">
                    <p className="text-gray-600 font-light leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <Calendar size={14} />
                          <span>{new Date(post.publishDate).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock size={14} />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* Category Filter - Hidden in Production */}
        {FEATURES.showAllFeatures && (
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {blogCategories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-3 rounded-full text-sm font-light tracking-wide transition-all duration-300 ${
                selectedCategory === category
                  ? "bg-gradient-to-r from-brand-main to-fire-main text-white shadow-md"
                  : "bg-white text-gray-600 hover:bg-gradient-to-r hover:from-brand-lighter hover:to-fire-lighter hover:text-brand-deep border border-gray-200"
              }`}
            >
              {category}
            </button>
          ))}
        </div>
        )}

        {/* All Posts Grid - Hidden in Production */}
        {FEATURES.showAllFeatures && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map((post) => (
            <article 
              key={post.id}
              className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group cursor-pointer"
              onClick={() => setSelectedPost(post)}
            >
              <div className="p-6">
                <div className="flex items-center space-x-2 mb-3">
                  <Tag size={14} className="text-spirit-teal" />
                  <span className="text-xs uppercase tracking-widest text-spirit-teal font-light">
                    {post.category}
                  </span>
                </div>
                
                <h4 className="text-lg font-light text-gray-900 mb-3 leading-relaxed group-hover:text-brand-main transition-colors">
                  {post.title}
                </h4>
                
                <p className="text-gray-600 font-light text-sm leading-relaxed mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-3">
                    <span>{new Date(post.publishDate).toLocaleDateString()}</span>
                    <span>•</span>
                    <span>{post.readTime}</span>
                  </div>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </article>
          ))}
        </div>
        )}

        {/* Development Note - Only in Test Mode */}
        {FEATURES.showAllFeatures && (
          <div className="mt-8 text-center">
            <div className="inline-block bg-purple-100 border-2 border-purple-300 rounded-lg px-6 py-3">
              <p className="text-sm text-purple-800 font-light">
                🔧 <span className="font-medium">Development Mode:</span> Blog cards visible for testing and content management
              </p>
            </div>
          </div>
        )}

        {/* Enhanced INSPIYAH MAIL Description - Level 10 Soulful */}
        <div className="mt-20 mb-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-br from-purple-900/90 via-orange-900/90 to-black/90 backdrop-blur-sm rounded-3xl p-12 border border-white/10 shadow-2xl">
              <Sparkles size={40} className="mx-auto mb-6 text-yellow-400" />
              <p className="text-xl lg:text-2xl font-light leading-relaxed text-white/95 mb-6">
                The <span className="font-medium text-yellow-300">INSPIYAH Mail</span> ignites divine sparks within your soul — sacred moments that awaken, align, and illuminate the infinite possibilities <span className="font-medium text-orange-300">YAH</span> has written for you.
              </p>
              <p className="text-lg font-light leading-relaxed text-white/90 mb-6">
                These messages are <span className="italic text-purple-300">seeds of light</span>, planting themselves deep in your spirit, preparing the soil for transformation.
              </p>
              <p className="text-lg font-light leading-relaxed text-white/90 mb-6">
                When you're ready, the <span className="font-medium text-orange-300">INSPIYAH Journey</span> becomes your sanctuary—where divine whispers become daily rituals, where revelation transforms into lifestyle, where your soul's calling unfolds into <span className="italic">sacred purpose</span>.
              </p>
              <p className="text-xl font-light leading-relaxed text-white/95 mb-2">
                <span className="text-yellow-300">From fleeting inspiration to eternal transformation.</span><br />
                <span className="text-orange-300">From message to manifestation.</span><br />
                <span className="text-red-300">From spark to sacred fire.</span>
              </p>
              <p className="text-2xl font-light mt-8 text-white">
                🔥 This is where you step into <span className="font-medium bg-gradient-to-r from-yellow-300 via-orange-300 to-red-400 bg-clip-text text-transparent">YAH HEALTH WEALTH HAPPINESS</span>, The Universal Pillars of Success. 🔥
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-brand-deep via-fire-main to-spirit-teal rounded-2xl p-12 text-white">
            <Sparkles size={32} className="mx-auto mb-6" />
            <h3 className="text-3xl font-light mb-4 tracking-wide">
              Stay Connected to the Fiyah
            </h3>
            <p className="text-lg font-light mb-8 max-w-2xl mx-auto leading-relaxed">
              Join the movement towards YAH HEALTH WEALTH HAPPINESS. Get notified when new 
              INSPIYAH MAIL drops directly to your inbox.
            </p>
            <button 
              onClick={() => setShowSubscribeModal(true)}
              className="bg-white text-brand-deep px-8 py-4 rounded-full font-light tracking-wide hover:bg-gray-100 transition-colors hover:scale-105 transform duration-300"
            >
              Subscribe to INSPIYAH MAIL
            </button>
          </div>
        </div>
      </div>

      {/* Subscription Modal */}
      {showSubscribeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-900 via-orange-900 to-black rounded-3xl p-8 md:p-12 max-w-md w-full border-2 border-yellow-400/30 shadow-2xl relative">
            {/* Close button */}
            <button
              onClick={() => {
                setShowSubscribeModal(false);
                setSubscribeStatus({ message: "", isError: false });
                setEmail("");
                setConsentChecked(false);
              }}
              className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
            >
              <span className="text-2xl">&times;</span>
            </button>

            {/* Icon */}
            <div className="text-center mb-6">
              <Sparkles size={48} className="mx-auto text-yellow-400 mb-4 animate-pulse" />
              <h3 className="text-3xl font-light text-white mb-2">
                Connect to the Fiyah 🔥
              </h3>
              <p className="text-white/80 font-light text-sm">
                Join the movement towards YAH HEALTH WEALTH HAPPINESS
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubscribe} className="space-y-4">
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-6 py-4 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 transition-colors"
                  required
                />
              </div>

              {/* Consent Checkbox */}
              <div className="flex items-start space-x-3 bg-white/5 border border-white/20 rounded-xl p-4">
                <input
                  type="checkbox"
                  id="subscribe-consent"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-white/30 text-yellow-400 focus:ring-yellow-400 focus:ring-offset-0 cursor-pointer"
                  required
                />
                <label htmlFor="subscribe-consent" className="text-white/80 text-sm font-light leading-relaxed cursor-pointer">
                  By submitting, I acknowledge that my data will be processed in accordance with the{' '}
                  <a href="/privacy-policy" target="_blank" className="text-yellow-300 hover:text-yellow-200 underline">Privacy Policy</a>,{' '}
                  <a href="/data-processing-addendum" target="_blank" className="text-yellow-300 hover:text-yellow-200 underline">Data Processing Addendum</a>, and{' '}
                  <a href="/ai-usage-policy" target="_blank" className="text-yellow-300 hover:text-yellow-200 underline">AI Usage Policy</a>.
                </label>
              </div>

              {/* Status Message */}
              {subscribeStatus.message && (
                <div className={`p-4 rounded-lg text-sm ${
                  subscribeStatus.isError 
                    ? 'bg-red-500/20 text-red-200 border border-red-400/30' 
                    : 'bg-green-500/20 text-green-200 border border-green-400/30'
                }`}>
                  {subscribeStatus.message}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white px-8 py-4 rounded-full font-light tracking-wide hover:shadow-xl transition-all hover:scale-105 transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!consentChecked}
              >
                Count Me In! ✨
              </button>
            </form>

            {/* Note */}
            <p className="text-center text-white/50 text-xs mt-6 font-light">
              Divine sparks of inspiration delivered to your inbox. Unsubscribe anytime.
            </p>
          </div>
        </div>
      )}

      {/* Custom styles for line clamping */}
      <style jsx>{`
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
};

export default Blog;